import {
  InteractiveComponent,
  LessonComponentBase,
  LessonDisplayMode
} from '../../content/models/LessonComponent';
import {
  PortfolioContextItem,
  PortfolioListItem,
  PortfolioMarkerViewModel
} from '../models/Portfolio';
import { cloneDeep, escapeRegExp, isEmpty, isNull } from 'lodash';

import { ApiPortfolioState } from '../../dashboard/models/ApiPortfolioState';
import { DataProxy } from '@apollo/client';
import FeatureAccessService from '../../shared/services/FeatureAccessService';
import GraphQL from '../gql';
import LessonComponentService from '../../content/services/LessonComponentService';
import { LessonType } from '../../content/models/LessonVm';
import { submittedPortfoliosData } from '../gql-types';

class LessonPortfolioService {
  private statuses = {
    passed: 'passed',
    failed: 'failed'
  };

  private statusOptions: { label: string; value: ApiPortfolioState }[] = [
    {
      label: 'submitted', //Learner has submitted the portfolio
      value: ApiPortfolioState.AssessmentSubmit
    },
    {
      label: 'markingStarted',
      value: ApiPortfolioState.AssessmentMark
    },
    {
      label: 'workRequested', //Marker has requested the learner for any work needed.
      value: ApiPortfolioState.AssessmentRetry
    },
    {
      label: 'passed', //Marker has locked the portfolio with Passed state
      value: ApiPortfolioState.AssessmentLock
    },
    {
      label: 'failed', //Marker has locked the portfolio with Failed state
      value: ApiPortfolioState.AssessmentLock
    }
  ];

  public hasSubmitted(state: ApiPortfolioState) {
    return (
      state === ApiPortfolioState.AssessmentSubmit ||
      state === ApiPortfolioState.AssessmentMark ||
      state === ApiPortfolioState.AssessmentLock
    );
  }

  public updateMutation(cache: DataProxy, data: PortfolioMarkerViewModel) {
    const newPortfolioMarker = data;
    const existingPortfolios = cache.readQuery<submittedPortfoliosData>({
      query: GraphQL.Query.LIST_SUBMITTED_PORTFOLIOS
    });

    const updatedPortfolios = cloneDeep(existingPortfolios?.submittedPortfolios);
    const portfolio = updatedPortfolios?.find(
      (u) =>
        u.userkey === newPortfolioMarker.userkey &&
        u.modulekey === newPortfolioMarker.modulekey &&
        u.layer === newPortfolioMarker.layer
    );
    if (existingPortfolios && newPortfolioMarker) {
      portfolio.state = newPortfolioMarker.state;
      if (newPortfolioMarker.member) {
        portfolio.marker.firstname = newPortfolioMarker.member.firstname;
        portfolio.marker.lastname = newPortfolioMarker.member.lastname;
        portfolio.marker.userkey = newPortfolioMarker.member.userkey;
      } else {
        Object.keys(portfolio.marker).forEach(
          (key) => (portfolio.marker[key] = null)
        );
      }
      cache.writeQuery<submittedPortfoliosData>({
        query: GraphQL.Query.LIST_SUBMITTED_PORTFOLIOS,
        data: {
          submittedPortfolios: updatedPortfolios
        }
      });
    }
  }

  private getSelectedLabel(value: PortfolioContextItem) {
    return (
      this.statusOptions.find((item) => item.value === value.state)?.label || ''
    );
  }

  public filterPortfoliosBy(
    portfolios: PortfolioContextItem[],
    searchText: string
  ) {
    if (!searchText) {
      return portfolios;
    }
    const regexp = new RegExp(escapeRegExp(searchText), 'i');
    return portfolios.filter(
      ({ firstname, lastname, module }) =>
        regexp.test(firstname) || regexp.test(lastname) || regexp.test(module)
    );
  }

  public filterByStatus(portfolios: PortfolioContextItem[], status: string[]) {
    if (isEmpty(status)) {
      return portfolios;
    }

    return portfolios.filter((portfolio) =>
      portfolio.state !== ApiPortfolioState.AssessmentLock
        ? status.includes(this.getSelectedLabel(portfolio))
        : portfolio.completed
        ? status.includes(this.statuses.passed)
        : status.includes(this.statuses.failed)
    );
  }

  public filterByMarker(portfolios: PortfolioContextItem[], marker: number[]) {
    if (isEmpty(marker)) {
      return portfolios;
    }

    return portfolios.filter((portfolio) =>
      marker.includes(portfolio.marker?.userkey)
    );
  }

  public canFinalize(components: LessonComponentBase[]) {
    let finalizeable = true;

    for (const component of components) {
      if (
        LessonComponentService.isInteractive(component) &&
        !LessonComponentService.isAutoMarking(component.type) &&
        (component as InteractiveComponent).settings.points > 0 &&
        component.answer &&
        (isNull(component.mark) || isNaN(component.mark))
      ) {
        finalizeable = false;
        break;
      }
    }

    return finalizeable;
  }

  public canOpenPortfolio(assignedToMe: boolean) {
    /* Master admin access users can open all assessments and
       Reviewers and master marker access users can open the assessments which are assigned to them */
    return this.isMasterMarker() || assignedToMe;
  }

  public isMasterMarker() {
    return (
      FeatureAccessService.hasMasterAccess ||
      FeatureAccessService.hasMasterMarkerAccess
    );
  }

  public getMarkerDisplayName(
    portfolio: PortfolioListItem,
    loggedInUserkey: number
  ): string {
    if (!portfolio) {
      return 'Unassigned';
    }

    const assignedToMe = portfolio.markerUserkey === loggedInUserkey;

    return assignedToMe
      ? 'Me'
      : portfolio.markerUserkey
      ? `${portfolio.markerFirstname} ${portfolio.markerLastname}`
      : 'Unassigned';
  }

  public omitSubmitAnswer(
    lessonType: LessonType,
    mode: LessonDisplayMode,
    quizLocked: boolean,
    state: ApiPortfolioState
  ) {
    return (
      (lessonType === 'quiz' && quizLocked) ||
      mode === 'mark' ||
      (lessonType === 'assessment' &&
        mode === 'learn' &&
        state !== ApiPortfolioState.AssessmentStart &&
        state !== ApiPortfolioState.AssessmentRetry)
    );
  }

  /** Portfolio can be updated to new lesson version attempt or not.
   * Do not show UPDATE button for the following scenarios:
   *    -> Assessment portfolio: B2-1193: Decided to not show for 'assessment' type
   *    -> Locked portfolio
   *    -> When there is an attempt on latest module version if lesson version is
   *       'single attempt' mode
   */
  public canMigratePortfolio(
    canBeUpdate: boolean,
    lessonType: LessonType,
    portfolioState: ApiPortfolioState
  ): boolean {
    return (
      lessonType !== 'assessment' &&
      portfolioState !== ApiPortfolioState.QuizLock &&
      canBeUpdate
    );
  }
}

export default new LessonPortfolioService();
